import { useMemo } from "react";
import { findColIdByTag } from "src/generator/utils/rowTags";
import { useSelector } from "src/redux/hooks";

import { OVERLAY_PREF } from "../constants";

interface LiftingEquipmentTargets {
  distinct?: string;
  certificates?: string;
}

interface Props {
  uuid: string;
  sectionIndex?: number;
  rowIndex?: number;
  rowUuid?: string;
  sharedIndex?: number;
  subSectionIndex?: number;
}

const useLiftingEquipmentTargets = ({
  uuid,
  sectionIndex,
  rowIndex,
  rowUuid,
  sharedIndex,
  subSectionIndex,
}: Props): LiftingEquipmentTargets => {
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);

  return useMemo(() => {
    if (typeof sectionIndex !== "number") return {};

    const currentSection =
      sections[sectionIndex]?.shared &&
      typeof sharedIndex === "number" &&
      sections[sectionIndex]?.sections
        ? sections[sectionIndex]?.sections?.[sharedIndex]
        : sections[sectionIndex];

    if (!currentSection) return {};

    let currentCols: Record<string, any> = {};

    const regRow = currentSection.rows?.[rowIndex ?? 0];
    if (regRow?.cols) {
      currentCols = regRow.cols;
    } else if (regRow?.subrows) {
      const bareUuid = uuid.startsWith(OVERLAY_PREF)
        ? uuid.slice(OVERLAY_PREF.length)
        : uuid;
      const matchingSubrow = (regRow.subrows as any[]).find(
        sub => sub.cols && (bareUuid in sub.cols || uuid in sub.cols),
      );
      if (matchingSubrow?.cols) {
        currentCols = matchingSubrow.cols;
      }
    } else if (
      currentSection.subSections &&
      typeof subSectionIndex === "number"
    ) {
      const sub = currentSection.subSections[subSectionIndex] as any;
      const subRow = sub?.rows?.[rowIndex ?? 0];

      if (subRow?.items && rowUuid) {
        const items = subRow.items;
        currentCols = items[rowUuid].cols;
      }
    }

    return {
      distinct: findColIdByTag(currentCols, "LQ_Distinct", uuid),
      certificates: findColIdByTag(currentCols, "LQ_CertNums", uuid),
    };
  }, [
    rowIndex,
    rowUuid,
    sectionIndex,
    sections,
    sharedIndex,
    subSectionIndex,
    uuid,
  ]);
};

export default useLiftingEquipmentTargets;
